import React, { useState, useEffect, useRef, useCallback } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { Container, Image, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import "./located.css";
import "bootstrap/dist/css/bootstrap.min.css";

import backarrow from "../../../assets/images/backarrow.jpg";
import logo from "../../../assets/images/MTDlogo.png";
import responsivebg from "../../../assets/images/responsive-bg.png";

import Stepper from "../Stepper";
import { API_URL } from "../../../api";
import log from "../../../utils/log";
import * as userActions from "../../../actions/user";
import { useCookies } from "../../../hooks/useCookies";
import Loader from '../../Utility/Loader';


export const Located = () => {
  const navigate = useNavigate();
  const isFetched = useRef(false);

  const { getCookie } = useCookies();
  const token = getCookie("token");

  const [options, setOptions] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [errorMessageCountry, setErrorMessageCountry] = useState("");
  const [errorMessageCity, setErrorMessageCity] = useState("");
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [userLocationFetched, setUserLocationFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    if (isFetched.current) return;
    isFetched.current = true;

    if (!token) return;
    fetchCountryOptions();
  }, [token]);

  const fetchCountryOptions = async () => {
    try {
      setLoader(true)
      const data = await userActions.getCountryOptions();
      setOptions(data);
      setCountryOptions(Object.keys(data));
      setLoadingCountries(false);
    } catch (error) {
      log.error("Failed to fetch country options:", error);
    } finally {
      setLoader(false)
    }
  };

  const fetchUserLocation = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}customer/users/locations`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setSelectedCountry(data.country);
      setSelectedCity(data.location_string);
      setSelectedCityId(data.id);

      if (data.country && options[data.country]) {
        setCityOptions(
          options[data.country]?.map((city) => ({
            label: city.location_string,
            id: city.id,
          }))
        );
      }
    } catch (error) {
      console.error("Failed to fetch user location:", error);
    }
  }, [token, options]);

  useEffect(() => {
    if (Object?.keys(options)?.length > 0 && !userLocationFetched) {
      fetchUserLocation();
      setUserLocationFetched(true);
    }
  }, [options, userLocationFetched, fetchUserLocation]);

  const handleCountryChange = (event, value) => {
    setSelectedCountry(value);
    setSelectedCity(null);
    setSelectedCityId(null);
    setCityOptions(
      options[value]?.map((city) => ({
        label: city.location_string,
        id: city.id,
      })) || []
    );
    setErrorMessageCountry("");
  };

  const handleCityChange = (event, value) => {
    setSelectedCity(value?.label || "");
    setSelectedCityId(value?.id || null);
    setErrorMessageCity("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    if (!selectedCountry) {
      setErrorMessageCountry("*Please fill out the required fields.");
    }

    if (!selectedCityId) {
      setErrorMessageCity("*Please fill out the required fields.");
    }

    if (selectedCountry && selectedCityId) {
      fetch(`${API_URL}customer/users/locations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getCookie("token")}`,
        },
        body: JSON.stringify({ location_id: selectedCityId }),
      })
        .then((response) => response.json())
        .then(() => {
          setLoading(false);
          navigate("/religion");
        })
        .catch((error) => {
          console.error("Error saving location:", error);
          setLoading(false);
        });
    } else {
      setLoading(false); // Stop loading if validation fails
    }
  };


  return (
    <div className="located-container">
      <div className="located-bg">
        <Image className="banner-image" src={responsivebg} alt="background" />
      </div>
      {loader && <Loader />}
      <Container className="located-main">
        <Container className="located-content">
          <Container className="logo-progressbar7">
            <Container className="logo-arrow7">
              <Image
                src={backarrow}
                className="backarrow"
                onClick={() => window.history.back()}
                alt="Back"
              />
              <Image
                src={logo}
                alt="Logo"
                className="logo"
                style={{ backgroundColor: "transparent" }}
              />
            </Container>
            <Stepper activeStep="Located" />
          </Container>
          <Container className="located-text">
            <p>Where are you located?</p>
          </Container>
          <Container style={{ width: "100%" }}>
            <div className="container-child1">
              <Container className="located-details mb-4">
                <Container>
                  <Autocomplete
                    options={countryOptions}
                    value={selectedCountry || null}
                    onChange={handleCountryChange}
                    loading={loadingCountries}
                    isOptionEqualToValue={(option, value) => option === value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Country"
                        variant="outlined"
                        InputLabelProps={{ shrink: false }}
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "10px",
                          border: "1px solid black",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "& .MuiInputBase-input": {
                            padding: "10px",
                            borderRadius: "20px",
                          },
                        }}
                        error={Boolean(errorMessageCountry)}
                        // helperText={errorMessageCountry}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCountries && (
                                <CircularProgress size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {errorMessageCountry && (
                    <p className="error-message">{errorMessageCountry}</p>
                  )}
                </Container>
              </Container>

              {selectedCountry && (
                <Container>
                  <Autocomplete
                    options={cityOptions}
                    value={
                      selectedCity
                        ? { label: selectedCity, id: selectedCityId }
                        : null
                    }
                    onChange={handleCityChange}
                    loading={loadingCities}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select City/Province"
                        variant="outlined"
                        InputLabelProps={{ shrink: false }}
                        sx={{
                          backgroundColor: "white",
                          borderRadius: "10px",
                          border: "1px solid black",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "& .MuiInputBase-input": {
                            padding: "10px",
                            borderRadius: "20px",
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCities && <CircularProgress size={20} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {errorMessageCity && (
                    <div className="error-message">
                      {errorMessageCity}
                    </div>
                  )}
                </Container>
              )}
            </div>
          </Container>
          <div className="d-flex justify-content-center next-btn">
            <button type="submit" className="global-next-bottom-fix-btn" onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" role="status" />
              ) : (
                "Next"
              )}
            </button>
          </div>
        </Container>
      </Container>
    </div>
  );
};
